import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BaseCellRendererComponent } from '@y42/ag-grid';
import { connectState } from '@y42/angular/util';
import { ICellRendererParams, RowGroupOpenedEvent } from 'ag-grid-community';
import { BehaviorSubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AssetSidebarService } from '../../asset-sidebar';
import { FeatureListViewDataWithChild } from '../abstract-feature-list-view.service.model';

export interface EntryGroupCellRendererParams {
  showLabelWhenNotTree?: boolean;
  showChildElements?: boolean;
  showLogo?: boolean;
  hideDetails?: boolean;
  rowGroupingEnabled?: boolean;
  showStar?: boolean;
}

type IEntryGroupCellRendererParams = ICellRendererParams<FeatureListViewDataWithChild, string> &
  EntryGroupCellRendererParams;

@UntilDestroy()
@Component({
  selector: 'y42-entry-group-cell-renderer',
  templateUrl: './entry-group-cell-renderer.component.html',
  styleUrls: ['./entry-group-cell-renderer.component.scss'],
  animations: [
    trigger('showAnimation', [
      transition(':enter', [
        style({ transform: 'translateY(15px)', opacity: 0 }),
        animate('250ms ease-out', style({ transform: 'none', opacity: '*' })),
      ]),
      transition(':leave', [
        style({ transform: 'none', opacity: '*' }),
        animate('250ms ease-out', style({ transform: 'translateY(15px)', opacity: 0 })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntryGroupCellRendererComponent
  extends BaseCellRendererComponent<string, FeatureListViewDataWithChild, IEntryGroupCellRendererParams>
  implements OnDestroy
{
  constructor(private readonly assetSidebarService: AssetSidebarService) {
    super();
  }

  private readonly isExpanded$$ = new BehaviorSubject(false);
  private readonly path$ = this.params$.pipe(
    map((params): string | undefined => {
      return params?.data?.$path || params?.node?.aggData.id;
    }),
  );

  readonly state = connectState(this, {
    path: this.path$,
    isActive: this.path$.pipe(switchMap(($path) => this.assetSidebarService.selectIsActive($path))),
    firstLeafChild: this.params$.pipe(map((params) => params.node.allLeafChildren?.[0]?.data)),
    params: this.params$,
    expanded: this.isExpanded$$,
    aggData: this.params$.pipe(map((params) => params.node.aggData)),
  });

  override agInit(params: IEntryGroupCellRendererParams): void {
    super.agInit(params);
    params.api.addEventListener('rowGroupOpened', this.setExpandedFromEvent);
  }

  ngOnDestroy() {
    this.params?.api.removeEventListener('rowGroupOpened', this.setExpandedFromEvent);
  }

  toggleOpen() {
    this.params?.node.setExpanded(!this.params.node.expanded);
  }

  override refresh(params: IEntryGroupCellRendererParams) {
    return super.refresh(params);
  }

  private readonly setExpandedFromEvent = (event: RowGroupOpenedEvent) => {
    if (event.rowIndex === this.params?.rowIndex) {
      this.isExpanded$$.next(event.node.expanded);
    }
  };
}
